// src/platform/pwa/installManager.js

import eventBus from '@/platform/core/eventBus'
import logger from '@/platform/core/logger'

class InstallManager {
  constructor() {
    this.deferredPrompt = null
    this.initialized = false
    this.isInstalled = false

    this.handleBeforeInstallPrompt =
      this.handleBeforeInstallPrompt.bind(this)

    this.handleAppInstalled =
      this.handleAppInstalled.bind(this)
  }

  init() {
    if (
      this.initialized ||
      typeof window === 'undefined'
    ) {
      return
    }

    this.detectInstalledState()

    window.addEventListener(
      'beforeinstallprompt',
      this.handleBeforeInstallPrompt
    )

    window.addEventListener(
      'appinstalled',
      this.handleAppInstalled
    )

    this.initialized = true

    logger.info(
      'Install manager initialized.',
      {
        isInstalled: this.isInstalled,
        canInstall: this.canInstall()
      }
    )

    this.emitStatus('initialized')
  }

  destroy() {
    if (
      !this.initialized ||
      typeof window === 'undefined'
    ) {
      return
    }

    window.removeEventListener(
      'beforeinstallprompt',
      this.handleBeforeInstallPrompt
    )

    window.removeEventListener(
      'appinstalled',
      this.handleAppInstalled
    )

    this.deferredPrompt = null
    this.initialized = false

    logger.info(
      'Install manager destroyed.'
    )
  }

  detectInstalledState() {
    if (typeof window === 'undefined') {
      return false
    }

    const standalone =
      window.matchMedia?.(
        '(display-mode: standalone)'
      )?.matches === true

    const iosStandalone =
      window.navigator?.standalone === true

    this.isInstalled =
      Boolean(
        standalone ||
        iosStandalone
      )

    return this.isInstalled
  }

  handleBeforeInstallPrompt(event) {
    event.preventDefault()

    this.deferredPrompt =
      event

    this.isInstalled =
      false

    logger.info(
      'Application installation is available.'
    )

    eventBus.emit(
      'platform.install.available',
      {
        canInstall: true,
        isInstalled: false,
        timestamp:
          new Date().toISOString()
      }
    )
  }

  handleAppInstalled() {
    this.isInstalled =
      true

    this.deferredPrompt =
      null

    logger.info(
      'Application installed successfully.'
    )

    eventBus.emit(
      'platform.install.completed',
      {
        canInstall: false,
        isInstalled: true,
        timestamp:
          new Date().toISOString()
      }
    )
  }

  async install() {
    this.detectInstalledState()

    if (this.isInstalled) {
      logger.debug(
        'Install request ignored because application is already installed.'
      )

      return {
        success: false,
        reason: 'already-installed'
      }
    }

    if (!this.deferredPrompt) {
      logger.warn(
        'Install request made but browser installation prompt is unavailable.'
      )

      eventBus.emit(
        'platform.install.unavailable',
        {
          reason:
            'install-prompt-not-available',

          timestamp:
            new Date().toISOString()
        }
      )

      return {
        success: false,
        reason:
          'install-prompt-not-available'
      }
    }

    const promptEvent =
      this.deferredPrompt

    this.deferredPrompt =
      null

    try {
      logger.info(
        'Opening native application install prompt.'
      )

      eventBus.emit(
        'platform.install.prompted',
        {
          timestamp:
            new Date().toISOString()
        }
      )

      await promptEvent.prompt()

      const choice =
        await promptEvent.userChoice

      if (
        choice.outcome ===
        'accepted'
      ) {
        logger.info(
          'User accepted application installation.'
        )

        eventBus.emit(
          'platform.install.accepted',
          {
            outcome:
              choice.outcome,

            timestamp:
              new Date().toISOString()
          }
        )
      } else {
        logger.info(
          'User dismissed application installation.'
        )

        eventBus.emit(
          'platform.install.dismissed',
          {
            outcome:
              choice.outcome,

            timestamp:
              new Date().toISOString()
          }
        )
      }

      return {
        success:
          choice.outcome ===
          'accepted',

        outcome:
          choice.outcome
      }
    } catch (error) {
      logger.error(
        'Application installation failed.',
        {
          error
        }
      )

      eventBus.emit(
        'platform.install.error',
        {
          error,
          timestamp:
            new Date().toISOString()
        }
      )

      return {
        success: false,
        reason: 'install-error',
        error
      }
    }
  }

  canInstall() {
    return Boolean(
      !this.isInstalled &&
      this.deferredPrompt
    )
  }

  installed() {
    return this.detectInstalledState()
  }

  getStatus() {
    return {
      isInstalled:
        this.detectInstalledState(),

      canInstall:
        this.canInstall()
    }
  }

  emitStatus(
    reason = 'status'
  ) {
    const status =
      this.getStatus()

    eventBus.emit(
      'platform.install.status',
      {
        reason,
        ...status,
        timestamp:
          new Date().toISOString()
      }
    )
  }
}

const installManager =
  new InstallManager()

export default installManager